import { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../context/AuthContext'
import { useNotifications } from '../context/NotificationContext'
import AlertBanner from './AlertBanner'
import {
  LayoutDashboard,
  Users,
  Phone,
  Key,
  UserCircle,
  Mic,
  Tags,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Link2,
  PanelLeftOpen,
  PanelLeftClose,
  Sun,
  Moon,
  Monitor,
  Activity,
  Server,
  BarChart3,
  Database,
  Building2,
  DollarSign,
  Inbox,
  AlertCircle,
  MapPin
} from 'lucide-react'
import './Layout.css'

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/issues', icon: AlertCircle, label: 'Issues' },
  { path: '/request-inbox', icon: Inbox, label: 'Requests', requiresAccess: 'access_to_clients' },
  { path: '/stats', icon: BarChart3, label: 'Statistics', requiresAccess: 'access_to_stats' },
  { path: '/monitoring-v3', icon: Activity, label: 'Monitoring', requiresAccess: 'access_to_monitor' },
  { path: '/servers', icon: Server, label: 'Servers', requiresAccess: 'access_to_servers' },
  { path: '/backup-stats', icon: Database, label: 'Backups', requiresAccess: 'access_to_backups' },
  { path: '/avatars', icon: UserCircle, label: 'Avatars', requiresAccess: 'access_to_avatars' },
  { path: '/dialers', icon: Phone, label: 'Dialers', requiresAccess: 'access_to_dialers' },
  { path: '/clients', icon: Building2, label: 'Clients', requiresAccess: 'access_to_clients' },
  { path: '/financials', icon: DollarSign, label: 'Financials', requiresAccess: 'access_to_financials' },
  { path: '/licenses', icon: Key, label: 'Licenses', requiresAccess: 'access_to_licenses' },
  { path: '/users', icon: Users, label: 'Users', adminOnly: true },
  { path: '/voice-sets', icon: Mic, label: 'Voice Sets', requiresAccess: 'access_to_voicesets' },
  { path: '/keyword-categories', icon: Tags, label: 'Keywords', requiresAccess: 'access_to_keywords' },
  { path: '/links', icon: Link2, label: 'Links', requiresAccess: 'access_to_links' },
  { path: '/templates', icon: FileText, label: 'Templates', requiresAccess: 'access_to_templates' },
  { path: '/machines', icon: Monitor, label: 'Machines', requiresAccess: 'access_to_machines' },
  { path: '/field-team', icon: MapPin, label: 'Field Team', requiresAccess: 'access_to_field_team' }
]

function Layout() {
  const { user, logout, isAdmin } = useAuth()
  const { pendingRequestCount } = useNotifications()
  const navigate = useNavigate()
  // Start with sidebar CLOSED by default
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Theme state: 'light', 'dark', or 'auto'
  const [themeMode, setThemeMode] = useState(() => {
    const isAuto = localStorage.getItem('redstone-theme-auto') !== 'false'
    if (isAuto) return 'auto'
    return localStorage.getItem('redstone-theme') || 'dark'
  })

  // Track actual applied theme (for logo selection)
  const [appliedTheme, setAppliedTheme] = useState('dark')

  useEffect(() => {
    const updateAppliedTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme') || 'dark'
      setAppliedTheme(theme)
    }
    updateAppliedTheme()
    // Watch for theme changes
    const observer = new MutationObserver(updateAppliedTheme)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])

  // Update theme when themeMode changes
  useEffect(() => {
    if (window.themeUtils) {
      if (themeMode === 'auto') {
        window.themeUtils.setAutoTheme()
      } else {
        window.themeUtils.setTheme(themeMode)
      }
    }
  }, [themeMode])

  const cycleTheme = () => {
    // Cycle: auto -> light -> dark -> auto
    if (themeMode === 'auto') setThemeMode('light')
    else if (themeMode === 'light') setThemeMode('dark')
    else setThemeMode('auto')
  }

  const getThemeIcon = () => {
    if (themeMode === 'auto') return Monitor
    if (themeMode === 'light') return Sun
    return Moon
  }

  const getThemeLabel = () => {
    if (themeMode === 'auto') return 'Auto'
    if (themeMode === 'light') return 'Light'
    return 'Dark'
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const filteredNavItems = navItems.filter(item => {
    // Check admin-only items
    if (item.adminOnly && !isAdmin) return false
    // Check access permissions
    if (item.requiresAccess && user?.[item.requiresAccess] !== true && user?.[item.requiresAccess] !== 1) return false
    return true
  })

  const sidebarVariants = {
    open: { width: 280, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
    closed: { width: 80, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } }
  }

  const labelVariants = {
    open: { opacity: 1, x: 0, display: 'block', transition: { delay: 0.1 } },
    closed: { opacity: 0, x: -10, transitionEnd: { display: 'none' } }
  }

  return (
    <div className="app-layout">
      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-btn"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <motion.aside
        className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''} ${!sidebarOpen ? 'collapsed' : ''}`}
        variants={sidebarVariants}
        animate={sidebarOpen ? 'open' : 'closed'}
        initial="closed"
        style={{ width: sidebarOpen ? 280 : 80 }}
      >
        {/* Logo */}
        <div className="sidebar-header">
          <motion.div
            className="logo"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/')}
          >
            <img
              src={appliedTheme === 'light' ? '/logo-light.png' : '/logo-dark.png'}
              alt="Redstone AI"
              className="logo-img"
            />
            <AnimatePresence>
              {sidebarOpen && (
                <motion.span
                  className="logo-text"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  Redstone AI
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Toggle button - inline when open */}
          {sidebarOpen && (
            <motion.button
              className="collapse-btn"
              onClick={() => setSidebarOpen(false)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Collapse sidebar"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <PanelLeftClose size={20} />
            </motion.button>
          )}
        </div>

        {/* Toggle button - below header when closed */}
        {!sidebarOpen && (
          <motion.div
            className="sidebar-toggle-collapsed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.button
              className="collapse-btn centered"
              onClick={() => setSidebarOpen(true)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Expand sidebar"
            >
              <PanelLeftOpen size={20} />
            </motion.button>
          </motion.div>
        )}

        {/* Navigation */}
        <nav className="sidebar-nav">
          {filteredNavItems.map((item, index) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''} ${item.path === '/request-inbox' && pendingRequestCount > 0 ? 'has-badge' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
              title={!sidebarOpen ? item.label : undefined}
            >
              {({ isActive }) => (
                <motion.div
                  className="nav-item-content"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 4 }}
                >
                  {isActive && (
                    <motion.div
                      className="nav-indicator"
                      layoutId="nav-indicator"
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                  <div className="nav-icon-wrapper">
                    <item.icon size={20} className="nav-icon" />
                    {item.path === '/request-inbox' && pendingRequestCount > 0 && (
                      <span className="nav-badge">{pendingRequestCount > 99 ? '99+' : pendingRequestCount}</span>
                    )}
                  </div>
                  <motion.span
                    className="nav-label"
                    variants={labelVariants}
                  >
                    {item.label}
                  </motion.span>
                </motion.div>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="sidebar-footer">
          <div className="user-info">
            <motion.div
              className="user-avatar"
              whileHover={{ scale: 1.05 }}
            >
              {(user?.name || user?.username)?.charAt(0).toUpperCase() || 'U'}
            </motion.div>
            <motion.div
              className="user-details"
              variants={labelVariants}
            >
              <span className="user-name">{user?.name || user?.username || 'User'}</span>
              <span className="user-role">{isAdmin ? 'Administrator' : 'User'}</span>
            </motion.div>
          </div>

          {/* Theme Toggle */}
          <motion.button
            className="theme-toggle-btn"
            onClick={cycleTheme}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            title={!sidebarOpen ? `Theme: ${getThemeLabel()}` : undefined}
          >
            {(() => {
              const ThemeIcon = getThemeIcon()
              return <ThemeIcon size={20} />
            })()}
            <motion.span variants={labelVariants}>{getThemeLabel()}</motion.span>
          </motion.button>

          <motion.button
            className="logout-btn"
            onClick={handleLogout}
            whileHover={{ scale: 1.02, backgroundColor: 'rgba(239, 68, 68, 0.15)' }}
            whileTap={{ scale: 0.98 }}
            title={!sidebarOpen ? 'Logout' : undefined}
          >
            <LogOut size={20} />
            <motion.span variants={labelVariants}>Logout</motion.span>
          </motion.button>
        </div>
      </motion.aside>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Main Content */}
      <motion.main
        className="main-content"
        initial={{ marginLeft: 80 }}
        animate={{
          marginLeft: sidebarOpen ? 280 : 80
        }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="page-container">
          <AlertBanner />
          <Outlet />
        </div>
      </motion.main>
    </div>
  )
}

export default Layout